<template>
  <div class="job-search-list">
    <div v-if="jobs.length" class="job-search-list__header">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h3>{{ totalLabel }}</h3>
        </div>
        <div class="sorting-list d-flex flex-row align-items-center">
          <p class="view-label">View:</p>
          <custom-icon-button
            icon="grid"
            :active="isOnGridView"
            @clicked="setView('grid')"
          ></custom-icon-button>
          <custom-icon-button
            icon="list-ul"
            :scale="1.7"
            :active="!isOnGridView"
            @clicked="setView('list')"
          ></custom-icon-button>
          <div class="vertical-line"></div>
          <b-nav-item-dropdown
            menu-class="header__nav"
            block
            right
            no-caret
            @show="show = true"
            @hide="show = false"
          >
            <template v-slot:button-content>
              <div class="d-flex align-items-center">
                <p class="sort-label">
                  <span>Sort by:</span><b>{{ sortBy }}</b>
                </p>
                <b-icon
                  icon="caret-down-fill"
                  variant="dark"
                  :scale="0.6"
                  aria-hidden="true"
                ></b-icon>
              </div>
            </template>

            <b-dropdown-item
              v-model="filter.sorting"
              v-for="sort in sorting"
              :key="sort.value"
              :class="{
                active: filter.sorting === sort.value
              }"
              @click="setSorting(sort.value)"
            >
              {{ sort.text }}
              <b-icon
                v-if="filter.sorting === sort.value"
                icon="check2"
                variant="primary"
                :scale="1.2"
              ></b-icon>
            </b-dropdown-item>
          </b-nav-item-dropdown>
        </div>
      </div>
    </div>
    <div class="job-search-list__content">
      <template v-if="isOnGridView">
        <template v-if="form.$busy">
          <b-row>
            <b-col md="3" v-for="i in 8" :key="i">
              <card-shimmer></card-shimmer>
            </b-col>
          </b-row>
        </template>
        <template v-else>
          <b-row no-gutter>
            <b-col sm="6" md="3" v-for="job in jobs" :key="job.id">
              <job-item :job="job" />
            </b-col>
          </b-row>
        </template>
      </template>
      <template v-else>
        <template v-if="form.$busy">
          <card-long-shimmer v-for="i in 4" :key="i"></card-long-shimmer>
        </template>
        <template v-else>
          <job-item-long
            v-for="job in jobs"
            :key="job.id"
            :job="job"
            :is-editable="false"
          />
        </template>
      </template>

      <template v-if="!form.$busy && !jobs.length">
        <b-row class="no-result text-center">
          <b-col>
            <b-img
              :src="require('@/assets/images/no-results.png')"
              width="150"
              height="150"
              class="no-result__icon"
            ></b-img>

            <template v-if="filter.search">
              <h1 class="no-result__heading">
                {{ `Sorry we couldn't find any matches for ` }}
                <b>{{ `‘${filter.search}’` }}</b>
              </h1>
              <h4 class="no-result__subheading">
                Please try searching with another term
              </h4>
            </template>
            <template v-else>
              <h1 class="no-result__heading">No results found</h1>
              <h4 class="no-result__subheading">
                Please try searching again later
              </h4>
            </template>
          </b-col>
        </b-row>
      </template>

      <custom-pagination
        :meta="jobsMeta"
        :loading="form.$busy"
        class="job-pagination"
        @change="setPage"
      ></custom-pagination>
    </div>
  </div>
</template>
<script>
/**
 * ==================================================================================
 * Job List
 * ==================================================================================
 **/

import { mapState, mapMutations, mapActions } from 'vuex'
import { find } from 'lodash'
import Form from '@/utils/form'
import JobItem from '@/components/cards/JobItem'
import JobItemLong from '@/components/cards/JobItemLong'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import CustomPagination from '@/components/inputs/CustomPagination'
import CardShimmer from '@/components/shimmers/card'
import CardLongShimmer from '@/components/shimmers/CardLong'

const VIEW = ['grid', 'list']

export default {
  components: {
    JobItem,
    JobItemLong,
    CustomIconButton,
    CustomPagination,
    CardShimmer,
    CardLongShimmer
  },
  computed: {
    ...mapState({
      jobs: state => state.job.list,
      jobsMeta: state => state.job.listMeta
    }),

    limit() {
      return this.isOnGridView ? 8 : 4
    },

    sortBy() {
      let sort = find(this.sorting, { value: this.filter.sorting })
      return sort ? sort.text : ''
    },

    totalLabel() {
      let items = this.jobsMeta && this.jobsMeta.total ? this.jobsMeta.total : 0
      return `${items} Result${items > 1 ? 's' : ''}`
    },

    isOnGridView() {
      return this.filter.view === VIEW[0]
    }
  },
  data: function() {
    return {
      form: new Form({}),
      filter: {
        search: null,
        view: null,
        sorting: null
      },

      sorting: [
        { value: '-created_at', text: 'Most recent' },
        { value: null, text: 'Best match' },
        { value: 'price', text: 'Cheapest' },
        { value: '-price', text: 'Most expensive' }
      ]
    }
  },
  methods: {
    ...mapActions({
      getJobs: 'job/getJobs',
      addAlert: 'alert/addAlert'
    }),
    ...mapMutations({
      clearJobs: 'job/clearJobList'
    }),

    init(page = 1) {
      this.clearJobs()
      this.fetchJobs(page)
    },

    async fetchJobs(page = 1) {
      if (this.form.$busy) return

      this.form.$setLoading(true)

      let params = {
        page: page,
        filters: this.filter,
        limit: this.limit
      }

      await this.getJobs(params)
      this.form.$setLoading(false)
    },

    setPage(page) {
      this.init(page)
    },

    setView(view) {
      if (this.filter.view === view) return
      this.filter.view = view
      this.init()
    },

    setSorting(sort) {
      if (this.filter.sorting === sort) return
      this.filter.sorting = sort
      this.init()
    },

    setFilter(filter) {
      if (!filter) return
      this.filter.search = filter.search
      this.filter.location = filter.location
      this.filter.within_distance_to = filter.within_distance_to
    }
  },

  async created() {
    this.filter.view = VIEW[0]
    this.filter.sorting = this.sorting[0].value
    this.setFilter(this.$route.params.filter)

    this.$root.$on('filterFeeds', filters => {
      this.setFilter(filters)
      this.init()
    })

    this.init()
  }
}
</script>
<style scoped lang="scss">
.job-search-list /deep/ {
  .no-result {
    margin: 0px auto 200px auto;

    &__icon {
      margin: 0px auto 50px auto;
    }

    &__heading,
    &__subheading {
      font-weight: normal;
    }

    &__heading {
      font-size: 2.25rem;
      margin-bottom: 20px;
    }
  }

  .vertical-line {
    height: 45px;
    border-right: 1px solid #cfd3d7;
  }

  .job-search-list__header {
    margin: 100px auto 50px auto;

    p,
    h3 {
      margin-bottom: 0;

      b {
        font-weight: 600;
        margin: 0 10px;
        color: black;
      }
    }

    p {
      font-size: 1.1rem;
      color: #6c6c6c;
    }

    .dropdown {
      list-style: none;

      .nav-link {
        padding-left: 0;
        padding-right: 0;
      }

      .dropdown-menu {
        min-width: 210px;
        border: none;
        border-radius: 10px;
        padding: 1rem 0;
        box-shadow: var(--box-shadow);

        > li {
          padding: 0.25rem 1rem;

          a {
            display: flex;
            border-color: #00000008;
            justify-content: space-between;
          }

          p {
            font-size: 0.95rem;
            color: var(--font-color);
          }
        }

        > li.active {
          a,
          p {
            font-weight: 600;
          }
        }
      }
    }

    .sorting-list {
      justify-content: flex-end;

      > * {
        margin-right: 20px;
      }

      > *:last-child {
        margin-right: 0px;
      }
    }
  }

  .job-search-list__content {
    .job-item,
    .job-item-long {
      min-width: 100%;
      margin-bottom: 50px;
    }
  }
}

@media (max-width: 768px) {
  .job-search-list {
    .job-search-list__content {
      .job-item,
      .job-item-long {
        margin-bottom: 20px;
      }
    }
  }
}

@media (max-width: 568px) {
  .job-search-list {
    .sorting-list {
      .view-label,
      .sort-label > span {
        display: none;
      }

      .sort-label > b {
        margin-left: 0;
      }

      .custom-icon-button:nth-child(2) {
        margin-right: 7.5px;
      }
    }
  }
}
</style>
